import { createColumnHelper } from "@tanstack/react-table";
import { T } from "@tolgee/react";
import { KrwSubtext } from "#/components/krw-subtext";
import type { SpotState } from "#/hooks/use-hyperliquid-account";
import { formatPrice, formatSize, toNumber } from "../../lib/formatters";
import { numericString } from "./lib/sorting-fns";

export type BalanceRowItem = SpotState["balances"][number];

const col = createColumnHelper<BalanceRowItem>();

function getUsdcValue(row: BalanceRowItem) {
	// USDC is the quote currency so entryNtl is always 0 — use total instead
	return row.coin === "USDC" ? toNumber(row.total) : toNumber(row.entryNtl);
}

export function createBalancesColumns(t: (key: string) => string) {
	return [
		col.accessor("coin", {
			header: () => <T keyName="balances.columns.coin" />,
			cell: ({ getValue }) => (
				<span className="text-label2-medium text-trade-text-strong">{getValue()}</span>
			),
		}),
		col.accessor((row) => toNumber(row.total), {
			id: "total",
			header: () => <T keyName="balances.columns.total" />,
			sortingFn: numericString,
			cell: ({ row }) => {
				const usdValue = getUsdcValue(row.original);
				return (
					<span className="text-label2-tabular-medium text-trade-text-secondary">
						{formatSize(row.original.total)} {row.original.coin}
						{usdValue > 0 && <KrwSubtext usd={usdValue} inline parens />}
					</span>
				);
			},
		}),
		col.accessor(
			(row) => Math.max(0, toNumber(row.total) - toNumber(row.hold)),
			{
				id: "available",
				header: () => <T keyName="balances.columns.available" />,
				sortingFn: numericString,
				cell: ({ row }) => {
					const available = Math.max(
						0,
						toNumber(row.original.total) - toNumber(row.original.hold),
					);
					return (
						<span className="text-label2-tabular-medium text-trade-text-secondary">
							{formatSize(available)} {row.original.coin}
						</span>
					);
				},
			},
		),
		col.accessor(getUsdcValue, {
			id: "usdcValue",
			header: () => <T keyName="balances.columns.usdc-value" />,
			sortingFn: numericString,
			cell: ({ getValue }) => {
				const value = getValue();
				return (
					<span className="text-label2-tabular-medium text-trade-text-secondary">
						{value > 0 ? `${formatPrice(String(value))} USDC` : "-"}
					</span>
				);
			},
		}),
		col.display({
			id: "pnl",
			enableSorting: false,
			header: () => <T keyName="balances.columns.pnl" />,
			cell: () => (
				<span
					className="text-trade-text-faint"
					title={t("balances.pnl-na-tooltip")}
				>
					N/A
				</span>
			),
		}),
	];
}
